import moment from 'moment';

export default class TextUtils {
    static camelCaseToNormal(text: string): any {
        if(text){
            let res = text;
            // insert a space before all caps
            res = res.replace(/([A-Z])/g, ' $1').trim();
            // uppercase the first character
            res = res.replace(/^./, function(str){ return str.toUpperCase(); })
            return res;
        } else return '';
    }

    static escapeHtml(value: string | null | undefined): string {
        if (value === null || value === undefined) return "";
        return String(value)
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#39;");
    }

    // Constructing an Intl.NumberFormat is roughly two orders of magnitude dearer than formatting
    // with one, and this is called per money cell inside table render loops. Cached per
    // locale/currency pair so a table of N rows builds one formatter rather than N.
    private static readonly currencyFormatters = new Map<string, Intl.NumberFormat>();

    static currencyFormatter(value: number, locale: string = "en-US", currency: string = "USD"): string {
        const key = `${locale}|${currency}`;
        let formatter = TextUtils.currencyFormatters.get(key);
        if (!formatter) {
            formatter = new Intl.NumberFormat(locale, { style: 'currency', currency: currency });
            TextUtils.currencyFormatters.set(key, formatter);
        }
        return formatter.format(value);
    }

    /**
     * Formats a fax number by automatically prepending "+1" for US/Canada numbers.
     * Preserves international numbers unchanged.
     *
     * @param input - The fax number input from the user
     * @returns Formatted fax number
     *
     * Examples:
     * - "5551234567" → "+15551234567"
     * - "15551234567" → "+15551234567"
     * - "+15551234567" → "+15551234567"
     * - "+44 20 1234 5678" → "+442012345678" (international, unchanged)
     */
    static formatFaxNumber(input: string): string {
        if (!input) {
            return input;
        }

        const hasPlus = input.startsWith('+');

        // Remove ALL non-digit characters (including all plus signs)
        const digitsOnly = input.replace(/\D/g, '');

        // Only format US/Canada numbers (10 digits or 11 digits starting with 1)
        if (digitsOnly.length === 10) {
            // 10 digits → add +1
            return '+1' + digitsOnly;
        } else if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
            // 11 digits starting with 1 → add +
            return '+' + digitsOnly;
        }

        // For other cases (international, invalid), preserve original plus if present
        return hasPlus ? '+' + digitsOnly : (digitsOnly || input);
    }

    /**
     * Whether a user-entered fax number is a plausible destination.
     *
     * A digit-count-only check isn't enough: mFax rejects numbers whose area code or
     * exchange begins with 0 or 1 (the NANP rule), and it rejects short numbers as bad
     * country codes. Both rejections are opaque to the user, so screen for them here.
     *
     * The non-obvious rejections:
     * - "11844661366" → false (area code 184 is not a valid NANP area code)
     * - "442071234567" → false (international without the leading '+')
     */
    static isValidFaxNumber(input: string | null | undefined): boolean {
        if (!input) return false;

        const digits = input.replace(/\D/g, '');

        // US/Canada, with or without the country-code 1: NANP forbids an area code or
        // exchange starting with 0 or 1.
        if (/^1?[2-9]\d{2}[2-9]\d{6}$/.test(digits)) return true;

        // Anything else has to be an explicit international number, at E.164 length.
        // Country code 1 is excluded because NANP destinations must satisfy the rule above.
        return input.trimStart().startsWith('+') && /^[^1]\d{10,14}$/.test(digits);
    }

    // Render an 11-digit NDC as the human-readable 5-4-2 form (e.g. 00280-0100-15).
    // Returns the input unchanged if it isn't exactly 11 digits — the UI is allowed
    // to display non-canonical NDCs verbatim rather than blanking them.
    static formatNdc11(ndc: string | null | undefined): string {
        if (!ndc || ndc.length !== 11) return ndc || '';
        return `${ndc.substr(0, 5)}-${ndc.substr(5, 4)}-${ndc.substr(9)}`;
    }

    // Inverse of formatNdc11: reduce an NDC to bare digits. External drug APIs key on the
    // unpunctuated 11-digit form, while the UI and the database both carry the hyphenated one.
    static normalizeNdc11(ndc: string | null | undefined): string {
        return (ndc ?? '').replace(/[^0-9]/g, '');
    }

    // Render a Last Shipped value as "MM/DD/YYYY" or "MM/DD/YYYY (EQV)" when the
    // most recent shipment was for a therapeutic equivalent. Empty string for null.
    static lastShippedFormatter(value: Date | string | null | undefined, isEquivalent: boolean | null | undefined): string {
        if (!value) return '';
        const date = moment.utc(value).format('L');
        return isEquivalent ? `${date} (EQV)` : date;
    }
}
